import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Subtle enterprise neural-network / connected-globe hero background.
 * A single wireframe icosphere (globe) with slowly orbiting connection nodes
 * and thin animated lines. Extremely lightweight — no post-processing, minimal geometry.
 */

const Globe = () => {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.y = state.clock.elapsedTime * 0.04;
    ref.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.15) * 0.06;
  });
  return (
    <group ref={ref}>
      <mesh>
        <icosahedronGeometry args={[2.4, 3]} />
        <meshBasicMaterial color="#4dd7ee" wireframe transparent opacity={0.12} />
      </mesh>
      <mesh>
        <icosahedronGeometry args={[2.4, 1]} />
        <meshBasicMaterial color="#4dd7ee" wireframe transparent opacity={0.18} />
      </mesh>
    </group>
  );
};

const OrbitNodes = ({ count = 14, radius = 3.1 }) => {
  const group = useRef();
  const nodes = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const phi = Math.acos(-1 + (2 * i) / count);
      const theta = Math.sqrt(count * Math.PI) * phi;
      arr.push(
        new THREE.Vector3(
          radius * Math.cos(theta) * Math.sin(phi),
          radius * Math.sin(theta) * Math.sin(phi),
          radius * Math.cos(phi)
        )
      );
    }
    return arr;
  }, [count, radius]);

  const lineGeom = useMemo(() => {
    const positions = [];
    // connect each node to its nearest 2 neighbours for a network feel
    for (let i = 0; i < nodes.length; i++) {
      const distances = nodes
        .map((n, j) => ({ j, d: nodes[i].distanceTo(n) }))
        .filter((x) => x.j !== i)
        .sort((a, b) => a.d - b.d)
        .slice(0, 2);
      for (const { j } of distances) {
        positions.push(nodes[i].x, nodes[i].y, nodes[i].z);
        positions.push(nodes[j].x, nodes[j].y, nodes[j].z);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, [nodes]);

  useFrame((state) => {
    if (!group.current) return;
    group.current.rotation.y = state.clock.elapsedTime * 0.06;
    group.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.12) * 0.08;
  });

  return (
    <group ref={group}>
      {nodes.map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]}>
          <sphereGeometry args={[0.045, 12, 12]} />
          <meshBasicMaterial color="#66e6f7" transparent opacity={0.9} />
        </mesh>
      ))}
      <line geometry={lineGeom}>
        <lineBasicMaterial color="#4dd7ee" transparent opacity={0.14} />
      </line>
    </group>
  );
};

/**
 * Gentle mouse-parallax rig — moves the whole scene slightly with pointer.
 */
const ParallaxRig = ({ children }) => {
  const group = useRef();
  useFrame((state) => {
    if (!group.current) return;
    const x = state.pointer.x * 0.25;
    const y = state.pointer.y * 0.18;
    group.current.rotation.y += (x - group.current.rotation.y) * 0.04;
    group.current.rotation.x += (-y - group.current.rotation.x) * 0.04;
  });
  return <group ref={group}>{children}</group>;
};

const Scene = () => (
  <>
    <ambientLight intensity={0.6} />
    <ParallaxRig>
      <Globe />
      <OrbitNodes count={16} radius={3.1} />
    </ParallaxRig>
  </>
);

const HeroScene = () => {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      aria-hidden
      data-testid="hero-3d-scene"
    >
      <Canvas
        camera={{ position: [0, 0, 7], fov: 45 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
      </Canvas>
    </div>
  );
};

export default HeroScene;
